import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { optimizedImg } from "@/lib/img";
import { SessionAdsGrid } from "@/components/ads-express-previous-sessions";
import { cn } from "@/lib/utils";
import {
  History, ChevronDown, ChevronUp, CalendarClock, Loader2, ArrowDownWideNarrow, Users, Sparkles, CheckCircle2,
} from "lucide-react";

// Task #2046 follow-up — master-only enriched "Recent generations" table on
// the brand Ads Express page (mockup-style list). Non-masters keep the
// existing Previous Sessions panel; this component is only rendered for
// masters and reads the master-gated /recent-summary endpoint.

type RunStatus = "queued" | "running" | "completed" | "partial" | "failed" | "cancelled";

interface RecentRun {
  id: string;
  status: RunStatus;
  createdAt: string | null;
  completedAt: string | null;
  personaNameSnapshot: string | null;
  productNameSnapshot: string | null;
  angleNameSnapshot: string | null;
  personaCharacterSelectorEnabled?: boolean | null;
  selectedPersonaCharacterNameSnapshot?: string | null;
  templateCount: number;
  thumbnails: string[];
  adsTotal: number;
  adsReady: number;
  adsFailed: number;
  // In-flight image edits on this run's ads (server resolves edit jobs back to
  // their containing run). Feeds the "Edits" column badge.
  editsInProgress: number;
  // Finished edits (last 24h) + latest completion time, so the green
  // "ready for review" badge can clear once the user views the results.
  editsReady: number;
  editsReadyAt: string | null;
}

// Viewing a run's results dismisses its "edits ready for review" badge (the
// old Render Queue dismissal table is gone). Stored per run as the last time
// the user expanded the results; edits finished after that re-show the badge.
const EDITS_SEEN_KEY = "recentGenerationsEditsSeen";
function readEditsSeen(): Record<string, number> {
  try {
    const parsed = JSON.parse(localStorage.getItem(EDITS_SEEN_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}
function markEditsSeen(runId: string): Record<string, number> {
  const map = readEditsSeen();
  map[runId] = Date.now();
  // Keep the map small — only the most recent 50 runs matter.
  const entries = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 50);
  const next = Object.fromEntries(entries);
  try {
    localStorage.setItem(EDITS_SEEN_KEY, JSON.stringify(next));
  } catch {
    /* best-effort */
  }
  return next;
}

const STATUS_META: Record<RunStatus, { label: string; className: string }> = {
  queued: { label: "Queued", className: "bg-muted text-muted-foreground" },
  running: { label: "In progress", className: "bg-primary/10 text-primary" },
  completed: { label: "Completed", className: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  partial: { label: "Partial", className: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
  failed: { label: "Failed", className: "bg-destructive/10 text-destructive" },
  cancelled: { label: "Cancelled", className: "bg-muted text-muted-foreground" },
};

function whenLabel(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function agoLabel(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function AdsExpressRecentGenerations({
  brandId,
  variant,
  excludeGenerationId,
  adUnits,
  onOpenRun,
}: {
  brandId: string;
  variant: "standard" | "kc";
  excludeGenerationId: string | null;
  adUnits: Array<{ id: string; name: string }>;
  onOpenRun: (generationId: string) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sort, setSort] = useState<"newest" | "oldest">("newest");
  const [open, setOpen] = useState(true);
  // "View results" expands the run inline with the full editable ads grid
  // (same tiles/edit tools as Previous Sessions).
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editsSeen, setEditsSeen] = useState<Record<string, number>>(() => readEditsSeen());

  const { data: runs = [], isLoading } = useQuery<RecentRun[]>({
    queryKey: [`/api/brands/${brandId}/ad-generations/recent-summary`, variant, excludeGenerationId ?? "none"],
    queryFn: async () => {
      const params = new URLSearchParams({ variant });
      if (excludeGenerationId) params.set("excludeId", excludeGenerationId);
      const res = await fetch(
        `/api/brands/${brandId}/ad-generations/recent-summary?${params.toString()}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Failed to load recent generations (${res.status})`);
      return res.json();
    },
    enabled: !!brandId,
    // Keep in-progress rows fresh without hammering the server when idle.
    refetchInterval: (q) =>
      (q.state.data ?? []).some(
        (r) => r.status === "queued" || r.status === "running" || (r.editsInProgress ?? 0) > 0,
      ) ? 5000 : 60000,
    staleTime: 15_000,
  });

  const adUnitNameById = useMemo(() => new Map(adUnits.map((u) => [u.id, u.name])), [adUnits]);

  const visible = useMemo(() => {
    const filtered = statusFilter === "all" ? runs : runs.filter((r) => r.status === statusFilter);
    const sorted = [...filtered].sort((a, b) => {
      const ta = new Date(a.createdAt ?? 0).getTime();
      const tb = new Date(b.createdAt ?? 0).getTime();
      return sort === "newest" ? tb - ta : ta - tb;
    });
    return sorted;
  }, [runs, statusFilter, sort]);

  if (!isLoading && runs.length === 0) return null;

  return (
    <Card className="p-5 space-y-4" data-testid="panel-recent-generations">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left"
          data-testid="button-toggle-recent-generations"
        >
          <h2 className="font-semibold flex items-center gap-2 text-sm">
            <History className="h-4 w-4 text-primary" /> Recent generations
            {!isLoading && (
              <Badge variant="secondary" className="text-[10px]" data-testid="badge-recent-generations-count">
                {runs.length}
              </Badge>
            )}
          </h2>
          {open ? (
            <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
        </button>
        {open && (
          <div className="ml-auto flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="filter-recent-generations-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v) => setSort(v as "newest" | "oldest")}>
              <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="filter-recent-generations-sort">
                <ArrowDownWideNarrow className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {open && isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      )}

      {open && !isLoading && (
        <div className="divide-y divide-border/60">
          {/* Column header */}
          <div className="hidden md:grid grid-cols-[100px_90px_minmax(200px,1.3fr)_100px_140px_minmax(140px,1fr)_100px_100px] items-center gap-3 pb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            <span>Run</span>
            <span>Templates</span>
            <span>Brief</span>
            <span>Status</span>
            <span>Edits</span>
            <span>Quick view</span>
            <span />
            <span className="text-right">Actions</span>
          </div>
          {visible.map((r) => {
            const meta = STATUS_META[r.status] ?? STATUS_META.queued;
            const inFlight = r.status === "queued" || r.status === "running";
            const extra = Math.max(0, r.adsTotal - r.thumbnails.length);
            const expanded = expandedId === r.id;
            return (
              <div key={r.id} data-testid={`recent-generation-row-${r.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-[100px_90px_minmax(200px,1.3fr)_100px_140px_minmax(140px,1fr)_100px_100px] items-center gap-3 py-3">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                  {whenLabel(r.createdAt)}
                </span>
                <span>
                  <Badge variant="outline" className="text-[10px] whitespace-nowrap">
                    {r.templateCount} template{r.templateCount === 1 ? "" : "s"}
                  </Badge>
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium truncate">
                    {r.productNameSnapshot || r.personaNameSnapshot || "Ad generation run"}
                  </span>
                  <span className="flex flex-col gap-0.5 text-xs text-muted-foreground min-w-0 md:flex-row md:flex-wrap md:items-center md:gap-x-3">
                    {r.personaNameSnapshot && (
                      <span className="inline-flex items-center gap-1 min-w-0">
                        <Users className="h-3 w-3 shrink-0" />
                        <span className="truncate">{r.personaNameSnapshot}</span>
                      </span>
                    )}
                     {variant === "standard" && r.personaCharacterSelectorEnabled === true && (
                       <Badge
                         variant="outline"
                         className="text-[10px]"
                         data-testid={`badge-recent-generation-character-${r.id}`}
                       >
                         {r.selectedPersonaCharacterNameSnapshot
                           ? `Character: ${r.selectedPersonaCharacterNameSnapshot}`
                           : "Character unavailable"}
                       </Badge>
                     )}
                    {r.angleNameSnapshot && (
                      <span className="inline-flex items-center gap-1 min-w-0">
                        <Sparkles className="h-3 w-3 shrink-0" />
                        <span className="truncate">{r.angleNameSnapshot}</span>
                      </span>
                    )}
                  </span>
                </span>
                <span className="flex flex-col items-start gap-0.5">
                  <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium", meta.className)}>
                    {inFlight && <Loader2 className="h-3 w-3 animate-spin" />}
                    {meta.label}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{agoLabel(r.completedAt ?? r.createdAt)}</span>
                </span>
                <span className="flex flex-col items-start gap-0.5">
                  {(r.editsInProgress ?? 0) > 0 ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-violet-500/10 text-violet-600 dark:text-violet-400"
                      data-testid={`badge-recent-generation-editing-${r.id}`}
                    >
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {r.editsInProgress} edit{r.editsInProgress === 1 ? "" : "s"} in progress
                    </span>
                  ) : null}
                  {(r.editsReady ?? 0) > 0 &&
                  r.editsReadyAt &&
                  new Date(r.editsReadyAt).getTime() > (editsSeen[r.id] ?? 0) ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      data-testid={`badge-recent-generation-edit-ready-${r.id}`}
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      {r.editsReady} edit{r.editsReady === 1 ? "" : "s"} ready for review
                    </span>
                  ) : null}
                  {(r.editsInProgress ?? 0) === 0 &&
                    !(
                      (r.editsReady ?? 0) > 0 &&
                      r.editsReadyAt &&
                      new Date(r.editsReadyAt).getTime() > (editsSeen[r.id] ?? 0)
                    ) && <span className="text-[11px] text-muted-foreground">—</span>}
                </span>
                <span className="flex items-center gap-1.5">
                  {r.thumbnails.map((url, i) => (
                    <span key={i} className="h-10 w-10 rounded-md overflow-hidden bg-muted border border-border/60 shrink-0">
                      <img src={optimizedImg(url, 120, 70)} alt="" className="h-full w-full object-cover" loading="lazy" />
                    </span>
                  ))}
                  {inFlight && r.thumbnails.length < Math.min(r.adsTotal || r.templateCount, 5) && (
                    <span className="h-10 w-10 rounded-md bg-muted border border-dashed border-border flex items-center justify-center shrink-0">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    </span>
                  )}
                  {extra > 0 && (
                    <span className="h-10 w-10 rounded-md bg-muted border border-border/60 flex items-center justify-center text-[11px] font-medium text-muted-foreground shrink-0">
                      +{extra}
                    </span>
                  )}
                  {!inFlight && r.adsTotal === 0 && (
                    <span className="text-xs text-muted-foreground">No ads</span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {r.templateCount} template{r.templateCount === 1 ? "" : "s"}
                  <br />
                  {r.adsReady} ad{r.adsReady === 1 ? "" : "s"}
                  {r.adsFailed > 0 && <span className="text-destructive"> · {r.adsFailed} failed</span>}
                </span>
                <span className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (inFlight) {
                        onOpenRun(r.id);
                        return;
                      }
                      // Expanding counts as reviewing. Edits that finish AFTER
                      // this moment re-show the badge (even while expanded),
                      // so a completion the user is watching still announces
                      // itself in the Edits column.
                      if (!expanded) setEditsSeen(markEditsSeen(r.id));
                      setExpandedId(expanded ? null : r.id);
                    }}
                    data-testid={`button-recent-generation-open-${r.id}`}
                  >
                    {inFlight ? "View progress" : expanded ? "Hide results" : "View results"}
                  </Button>
                </span>
              </div>
              {expanded && (
                <div className="pb-4" data-testid={`recent-generation-expanded-${r.id}`}>
                  <SessionAdsGrid
                    sessionId={r.id}
                    adUnitNameById={adUnitNameById}
                    brandId={brandId}
                  />
                </div>
              )}
              </div>
            );
          })}
          {visible.length === 0 && (
            <p className="py-4 text-sm text-muted-foreground">No runs match this filter.</p>
          )}
          <p className="pt-3 text-center text-xs text-muted-foreground">You've reached the end</p>
        </div>
      )}
    </Card>
  );
}
